import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserCog, LogOut, FolderPlus, Folder, Upload, Trash2, Pencil, Download, X,
  FileText, File as FileIcon, Image as ImgIcon, Sparkles, Search, ArrowLeft, Loader2, Save,
  ChevronUp, ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import { staffApi, getStaffToken, clearStaffToken, STAFF_API } from "@/library/lib/staffApi";
import { resolveLogoUrl } from "@/library/lib/settings";

const ext_icon = (ext) => {
  const e = (ext || "").toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(e)) return { Icon: ImgIcon, tone: "text-fuchsia-600 bg-fuchsia-50" };
  if (e === "pdf") return { Icon: FileText, tone: "text-rose-600 bg-rose-50" };
  if (["doc", "docx"].includes(e)) return { Icon: FileText, tone: "text-blue-600 bg-blue-50" };
  if (["xls", "xlsx", "csv"].includes(e)) return { Icon: FileText, tone: "text-emerald-600 bg-emerald-50" };
  if (["ppt", "pptx"].includes(e)) return { Icon: FileText, tone: "text-orange-600 bg-orange-50" };
  if (["zip", "rar", "7z"].includes(e)) return { Icon: FileIcon, tone: "text-slate-600 bg-slate-100" };
  return { Icon: FileIcon, tone: "text-slate-600 bg-slate-100" };
};

const fmt_size = (b) => {
  if (!b) return "0 KB";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmt_date = (iso) => {
  try { return new Date(iso).toLocaleDateString("ar-SA", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function StaffDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState({ folders: 0, files: 0, last_upload: null });
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [subfolders, setSubfolders] = useState([]);
  const [parentChain, setParentChain] = useState([]); // مسار المجلدات الأب حتى الوصول للمجلد الحالي
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameFolder, setRenameFolder] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!getStaffToken()) { nav("/library/staff/login"); return; }
    (async () => {
      try {
        const [m, s, fs] = await Promise.all([
          staffApi.get("/auth/me"), staffApi.get("/stats/me"), staffApi.get("/folders")
        ]);
        setMe(m.data); setStats(s.data); setFolders(fs.data);
      } catch {
        nav("/library/staff/login");
      }
    })();
  }, [nav]);

  const refreshFolders = async () => {
    const [s, fs] = await Promise.all([staffApi.get("/stats/me"), staffApi.get("/folders")]);
    setStats(s.data); setFolders(fs.data);
  };

  const navigateToFolder = async (folder, chain) => {
    setSelectedFolder(folder);
    setParentChain(chain);
    const [subs, filesRes] = await Promise.all([
      staffApi.get("/folders", { params: { parent_id: folder.id } }),
      staffApi.get("/files", { params: { folder_id: folder.id, q: q || undefined } }),
    ]);
    setSubfolders(subs.data);
    setFiles(filesRes.data);
  };

  const openFolder = async (folder) => {
    await navigateToFolder(folder, []); // فتح من الشريط الجانبي = مستوى جذري
  };

  const enterSubfolder = async (sub) => {
    await navigateToFolder(sub, [...parentChain, selectedFolder]);
  };

  const goBack = async () => {
    if (parentChain.length > 0) {
      const prev = parentChain[parentChain.length - 1];
      await navigateToFolder(prev, parentChain.slice(0, -1));
    } else {
      setSelectedFolder(null); setSubfolders([]); setFiles([]); setParentChain([]);
    }
  };

  const moveRootFolder = async (folderId, direction, e) => {
    e.stopPropagation();
    try {
      await staffApi.post(`/folders/${folderId}/move`, null, { params: { direction } });
      await refreshFolders();
    } catch { /* ignore */ }
  };

  const moveSubfolder = async (folderId, direction, e) => {
    e.stopPropagation();
    try {
      await staffApi.post(`/folders/${folderId}/move`, null, { params: { direction } });
      await navigateToFolder(selectedFolder, parentChain);
    } catch { /* ignore */ }
  };

  const doSearch = async () => {
    if (!selectedFolder) return;
    const { data } = await staffApi.get("/files", { params: { folder_id: selectedFolder.id, q: q || undefined } });
    setFiles(data);
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const parent_id = selectedFolder ? selectedFolder.id : null;
      await staffApi.post("/folders", { name: newFolderName.trim(), parent_id });
      setNewFolderName(""); setShowNewFolder(false);
      if (selectedFolder) {
        await navigateToFolder(selectedFolder, parentChain);
      } else {
        await refreshFolders();
      }
      toast.success("تم إنشاء المجلد");
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر الإنشاء"); }
  };

  const doRenameFolder = async () => {
    if (!renameFolder || !renameValue.trim()) return;
    try {
      await staffApi.put(`/folders/${renameFolder.id}`, { name: renameValue.trim() });
      setRenameFolder(null); setRenameValue("");
      await refreshFolders();
      toast.success("تم التعديل");
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر التعديل"); }
  };

  const doDeleteFolder = async (f) => {
    if (!window.confirm(`حذف المجلد "${f.name}" وجميع ملفاته؟`)) return;
    try {
      await staffApi.delete(`/folders/${f.id}`);
      if (selectedFolder?.id === f.id) { setSelectedFolder(null); setFiles([]); }
      await refreshFolders();
      toast.success("تم الحذف");
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر الحذف"); }
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadTitle.trim() || !selectedFolder) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("title", uploadTitle.trim());
    fd.append("description", uploadDesc);
    fd.append("folder_id", selectedFolder.id);
    fd.append("file", uploadFile);
    try {
      await staffApi.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع الملف");
      setUploadTitle(""); setUploadDesc(""); setUploadFile(null); setShowUpload(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await openFolder(selectedFolder);
      await refreshFolders();
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر الرفع"); }
    finally { setUploading(false); }
  };

  const doDeleteFile = async (f) => {
    if (!window.confirm(`حذف الملف "${f.title}"؟`)) return;
    try {
      await staffApi.delete(`/files/${f.id}`);
      toast.success("تم الحذف");
      await openFolder(selectedFolder);
      await refreshFolders();
    } catch (err) { toast.error(err.response?.data?.detail || "تعذر الحذف"); }
  };

  const downloadFile = (f) => {
    const url = `${STAFF_API}/files/${f.id}/download`;
    window.open(url, "_blank");
  };

  const logout = () => { clearStaffToken(); nav("/"); };

  return (
    <div className="library-scope min-h-screen" dir="rtl" data-testid="staff-dashboard">
      {/* Topbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-dark to-gold flex items-center justify-center">
              <UserCog className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-brand-dark leading-tight text-[15px]">لوحة الموظف</div>
              <div className="text-[11px] text-slate-500">وثائق منسوبي المدرسة</div>
            </div>
          </div>
          <button onClick={logout} data-testid="staff-logout" className="text-xs font-heading px-3 py-1.5 rounded-lg text-rose-700 border border-rose-200 hover:bg-rose-50 flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> خروج
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Profile hero */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="premium-card p-5 sm:p-7 flex flex-col sm:flex-row items-center sm:items-start gap-5">
          <div className="shrink-0">
            {me?.photo_url ? (
              <img src={resolveLogoUrl(me.photo_url)} alt={me.full_name} className="w-24 h-24 rounded-2xl object-cover ring-2 ring-gold/30 shadow-md" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-brand/10 to-gold/10 flex items-center justify-center ring-2 ring-brand/10">
                <UserCog className="w-10 h-10 text-brand" />
              </div>
            )}
          </div>
          <div className="flex-1 text-center sm:text-right">
            <div className="text-[11px] text-gold-dark font-heading">مرحباً</div>
            <h1 className="mt-1 font-heading text-2xl font-extrabold text-brand-dark tracking-tight" data-testid="staff-fullname">{me?.full_name || "..."}</h1>
            <div className="mt-1 flex flex-wrap justify-center sm:justify-start items-center gap-2 text-slate-600 text-sm">
              <span className="inline-flex items-center gap-1 bg-brand/5 text-brand px-2 py-0.5 rounded-full text-xs font-heading">{me?.job_title || "—"}</span>
              {me?.department && <span className="text-xs text-slate-500">{me.department}</span>}
              {me?.employee_id && <span className="text-xs text-slate-400 tabular-nums">#{me.employee_id}</span>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 min-w-full sm:min-w-[340px]">
            <StatCard label="المجلدات" value={stats.folders} tone="brand" testid="stat-folders" />
            <StatCard label="الوثائق" value={stats.files} tone="gold" testid="stat-files" />
            <StatCard label="آخر رفع" value={stats.last_upload ? fmt_date(stats.last_upload.created_at) : "—"} tone="slate" small testid="stat-last" />
          </div>
        </motion.div>

        {/* Folders + files */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Folders sidebar */}
          <div className="premium-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-bold text-brand-dark flex items-center gap-1.5"><Folder className="w-4 h-4" /> مجلداتي</div>
              <button onClick={() => setShowNewFolder(true)} data-testid="btn-new-folder" className="text-xs font-heading px-2.5 py-1 rounded-lg bg-brand text-white hover:bg-brand-light flex items-center gap-1">
                <FolderPlus className="w-3.5 h-3.5" /> جديد
              </button>
            </div>
            <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
              {folders.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-6">لا توجد مجلدات بعد — أنشئ أول مجلد.</div>
              )}
              {folders.map((f) => (
                <div key={f.id} className={`group rounded-lg px-2.5 py-2 flex items-center gap-2 cursor-pointer transition-colors ${selectedFolder?.id === f.id ? "bg-brand/10 border border-brand/30" : "hover:bg-slate-50 border border-transparent"}`}>
                  <button onClick={() => openFolder(f)} data-testid={`folder-${f.id}`} className="flex-1 flex items-center gap-2 text-right min-w-0">
                    <Folder className={`w-4 h-4 shrink-0 ${selectedFolder?.id === f.id ? "text-brand" : "text-gold-dark"}`} />
                    <span className="text-sm text-slate-800 truncate" title={f.name}>{f.name}</span>
                    <span className="text-[10px] text-slate-400 tabular-nums shrink-0">{f.file_count}</span>
                  </button>
                  <button onClick={(e) => moveRootFolder(f.id, "up", e)} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-brand/10 hover:border-brand/40 text-slate-500 hover:text-brand transition-colors shrink-0" title="لأعلى"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={(e) => moveRootFolder(f.id, "down", e)} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-brand/10 hover:border-brand/40 text-slate-500 hover:text-brand transition-colors shrink-0" title="لأسفل"><ChevronDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => { setRenameFolder(f); setRenameValue(f.name); }} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-brand/10 hover:border-brand/40 text-slate-500 hover:text-brand transition-colors shrink-0" title="تعديل"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => doDeleteFolder(f)} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-rose-50 hover:border-rose-300 text-slate-500 hover:text-rose-600 transition-colors shrink-0" title="حذف"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Files main */}
          <div className="premium-card p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="font-heading font-bold text-brand-dark flex items-center gap-2 truncate min-w-0">
                {selectedFolder ? (
                  <>
                    {(parentChain.length > 0) && (
                      <button onClick={goBack} className="p-1 rounded-lg hover:bg-slate-100 text-slate-600" title="رجوع" data-testid="btn-back-folder">
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                    )}
                    <Folder className="w-4 h-4 text-gold-dark shrink-0" />
                    <span className="truncate">
                      {parentChain.map(p => p.name).join(" / ")}{parentChain.length > 0 ? " / " : ""}{selectedFolder.name}
                    </span>
                  </>
                ) : (
                  <><Sparkles className="w-4 h-4 text-gold" /> اختر مجلداً لعرض ملفاته</>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="بحث في الملفات..." className="pe-8 ps-3 py-1.5 rounded-lg border border-slate-200 text-xs w-40 sm:w-52 focus:border-brand outline-none" data-testid="search-files" />
                </div>
                <button onClick={() => selectedFolder && setShowNewFolder(true)} disabled={!selectedFolder} data-testid="btn-new-subfolder" className="text-xs font-heading px-3 py-1.5 rounded-lg bg-brand/10 text-brand hover:bg-brand/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                  <FolderPlus className="w-3.5 h-3.5" /> مجلد فرعي
                </button>
                <button onClick={() => selectedFolder && setShowUpload(true)} disabled={!selectedFolder} data-testid="btn-upload-file" className="text-xs font-heading px-3 py-1.5 rounded-lg bg-gold text-white hover:bg-gold-light disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" /> رفع ملف
                </button>
              </div>
            </div>

            {!selectedFolder ? (
              <div className="py-16 text-center text-slate-400">
                <Folder className="w-14 h-14 mx-auto text-slate-200" />
                <p className="mt-3 text-sm">اختر مجلداً من القائمة الجانبية لعرض ملفاته.</p>
              </div>
            ) : subfolders.length === 0 && files.length === 0 ? (
              <div className="py-16 text-center text-slate-400">
                <FileIcon className="w-14 h-14 mx-auto text-slate-200" />
                <p className="mt-3 text-sm">لا توجد ملفات أو مجلدات فرعية — استخدم الأزرار أعلى لإضافة أول عنصر.</p>
              </div>
            ) : (
              <div>
                {subfolders.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5 mb-4">
                    {subfolders.map((sub) => (
                      <div key={sub.id} className="group rounded-lg border border-slate-200 hover:border-brand/40 hover:bg-brand/5 transition-colors p-2.5 flex items-center gap-2 cursor-pointer" data-testid={`subfolder-${sub.id}`}>
                        <button onClick={() => enterSubfolder(sub)} className="flex-1 flex items-center gap-2 text-right min-w-0">
                          <Folder className="w-4 h-4 text-gold-dark shrink-0" />
                          <span className="text-sm text-slate-800 truncate" title={sub.name}>{sub.name}</span>
                          <span className="text-[10px] text-slate-400 tabular-nums shrink-0">{sub.file_count}</span>
                        </button>
                        <button onClick={(e) => moveSubfolder(sub.id, "up", e)} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-brand/10 hover:border-brand/40 text-slate-500 hover:text-brand transition-colors shrink-0" title="لأعلى"><ChevronUp className="w-3 h-3" /></button>
                        <button onClick={(e) => moveSubfolder(sub.id, "down", e)} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-brand/10 hover:border-brand/40 text-slate-500 hover:text-brand transition-colors shrink-0" title="لأسفل"><ChevronDown className="w-3 h-3" /></button>
                        <button onClick={() => { setRenameFolder(sub); setRenameValue(sub.name); }} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-brand/10 hover:border-brand/40 text-slate-500 hover:text-brand transition-colors shrink-0" title="تعديل"><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => doDeleteFolder(sub)} className="flex items-center justify-center w-6 h-6 rounded-md border border-slate-200 bg-white hover:bg-rose-50 hover:border-rose-300 text-slate-500 hover:text-rose-600 transition-colors shrink-0" title="حذف"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {files.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {files.map((f) => {
                  const { Icon, tone } = ext_icon(f.extension);
                  return (
                    <motion.div key={f.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="premium-card p-4 group">
                      <div className="flex items-start gap-3">
                        {f.thumb_path ? (
                          <img src={`${STAFF_API}/files/${f.id}/thumb`} alt="" className="w-14 h-16 object-cover rounded-md ring-1 ring-slate-200 shrink-0" />
                        ) : (
                          <div className={`w-14 h-16 rounded-md flex items-center justify-center shrink-0 ${tone}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-bold text-brand-dark text-sm leading-snug truncate" title={f.title}>{f.title}</div>
                          {f.description && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{f.description}</div>}
                          <div className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-400 tabular-nums">
                            <span className="uppercase font-mono">{f.extension}</span>
                            <span>•</span>
                            <span>{fmt_size(f.size)}</span>
                            <span>•</span>
                            <span>{fmt_date(f.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-1.5">
                        <button onClick={() => downloadFile(f)} data-testid={`file-view-${f.id}`} className="flex-1 text-xs font-heading px-2.5 py-1.5 rounded-lg bg-brand/5 hover:bg-brand/10 text-brand flex items-center justify-center gap-1">
                          <Download className="w-3.5 h-3.5" /> فتح / تنزيل
                        </button>
                        <button onClick={() => doDeleteFile(f)} className="px-2 py-1.5 rounded-lg text-rose-600 hover:bg-rose-50 border border-rose-100" title="حذف"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* New folder modal */}
      {showNewFolder && (
        <Modal title={selectedFolder ? `مجلد فرعي داخل "${selectedFolder.name}"` : "مجلد جديد"} onClose={() => setShowNewFolder(false)}>
          <input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="اسم المجلد" data-testid="input-folder-name" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-brand outline-none" autoFocus />
          <div className="mt-4 flex gap-2 justify-end">
            <button onClick={() => setShowNewFolder(false)} className="px-4 py-1.5 text-sm rounded-lg text-slate-600 hover:bg-slate-100">إلغاء</button>
            <button onClick={createFolder} data-testid="btn-create-folder" className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:bg-brand-light flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> حفظ</button>
          </div>
        </Modal>
      )}

      {/* Rename folder modal */}
      {renameFolder && (
        <Modal title="تعديل اسم المجلد" onClose={() => setRenameFolder(null)}>
          <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-brand outline-none" autoFocus />
          <div className="mt-4 flex gap-2 justify-end">
            <button onClick={() => setRenameFolder(null)} className="px-4 py-1.5 text-sm rounded-lg text-slate-600 hover:bg-slate-100">إلغاء</button>
            <button onClick={doRenameFolder} className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:bg-brand-light flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> حفظ</button>
          </div>
        </Modal>
      )}

      {/* Upload modal */}
      {showUpload && (
        <Modal title={`رفع ملف — ${selectedFolder?.name}`} onClose={() => setShowUpload(false)}>
          <form onSubmit={doUpload} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">عنوان الملف</label>
              <input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} required data-testid="upload-title" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-brand outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">وصف مختصر (اختياري)</label>
              <textarea value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-brand outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">الملف</label>
              <input ref={fileInputRef} type="file" onChange={(e) => setUploadFile(e.target.files[0])} required data-testid="upload-file-input" className="w-full text-sm" />
              <p className="text-[10px] text-slate-400 mt-1">جميع الأنواع مسموحة (PDF, Word, Excel, PPT, صور, ZIP...)</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowUpload(false)} className="px-4 py-1.5 text-sm rounded-lg text-slate-600 hover:bg-slate-100">إلغاء</button>
              <button type="submit" disabled={uploading} data-testid="btn-do-upload" className="px-4 py-1.5 text-sm rounded-lg bg-gold text-white hover:bg-gold-light disabled:opacity-60 flex items-center gap-1.5">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} رفع
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function StatCard({ label, value, tone, small, testid }) {
  const toneCls = tone === "brand" ? "from-brand-dark to-brand text-white" : tone === "gold" ? "from-gold-dark to-gold text-white" : "bg-slate-100 text-slate-800";
  return (
    <div data-testid={testid} className={`rounded-2xl px-3 py-3 text-center ring-1 ring-slate-200/40 ${tone === "slate" ? toneCls : `bg-gradient-to-br ${toneCls}`}`}>
      <div className={`font-heading font-black tabular-nums ${small ? "text-sm" : "text-2xl"} leading-tight`}>{value}</div>
      <div className={`mt-0.5 text-[11px] ${tone === "slate" ? "text-slate-500" : "text-white/90"}`}>{label}</div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-extrabold text-brand-dark">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}
